import * as React from "react";
import { Modal, notification } from "antd";
import {
  useGetServicesQuery,
  useGetRegionsQuery,
  useGetTypesQuery,
  useGetDataItemsQuery,
  useGetChildrenQuery,
  useGetNestedItemsQuery,
} from "../../../../store/api/browserApi";
import {
  BrowserServiceModalProps,
  BrowserLink,
  BrowserData,
  ColumnType,
} from "./types";
import { useBrowserState } from "./hooks/useBrowserState";
import { useKeyboardNavigation } from "./hooks/useKeyboardNavigation";
import { BrowserBreadcrumb } from "./components/BrowserBreadcrumb";
import { BrowserColumn } from "./components/BrowserColumn";
import { ModalFooter } from "./components/ModalFooter";
import styles from "./BrowserServiceModal.module.scss";

export const BrowserServiceModal: React.FC<BrowserServiceModalProps> = ({
  title,
  open,
  selectionType,
  onClose,
  cloudaccountLink = "",
  forGcpResource = { isActive: false, cloudaccountLink: "" },
  forAzureScope = { isActive: false, cloudaccountLink: "" },
  restrictToBucket = false,
  restrictToParent = false,
  restrictToVPCEndpointService = false,
  dataTypeRestrictions = [],
}) => {
  const [isConfirmLoading, setIsConfirmLoading] = React.useState(false);

  // Determine effective cloud account link
  const effectiveCloudaccountLink = React.useMemo(() => {
    if (forGcpResource.isActive) return forGcpResource.cloudaccountLink;
    if (forAzureScope.isActive) return forAzureScope.cloudaccountLink;
    return cloudaccountLink;
  }, [forGcpResource, forAzureScope, cloudaccountLink]);

  // State management
  const {
    state,
    selectItem,
    setFilter,
    setFocusedIndex,
    setActiveColumn,
    toggleSelection,
    setSingleSelection,
    clearSelections,
    navigateToBreadcrumb,
    reset,
  } = useBrowserState();

  // Column refs for keyboard navigation
  const columnRefs = React.useRef<Map<ColumnType, HTMLDivElement | null>>(new Map());
  const searchInputRefs = React.useRef<Map<ColumnType, HTMLInputElement | null>>(new Map());

  // RTK Query hooks with conditional fetching
  // Using currentData instead of data to avoid showing stale cached data while fetching
  // Using refetchOnMountOrArgChange to ensure fresh data on each selection
  const shouldFetchServices = open;
  const { currentData: services, isLoading: servicesLoading, error: servicesError } = useGetServicesQuery(
    effectiveCloudaccountLink,
    { skip: !shouldFetchServices || !effectiveCloudaccountLink, refetchOnMountOrArgChange: true }
  );

  // Auto-select service based on restrictions
  React.useEffect(() => {
    if (!services || services.length === 0 || state.columns.services.selectedHref) return;

    // Auto-select VPC service (AWS) or compute service (GCP) when restrictToVPCEndpointService is true
    if (restrictToVPCEndpointService) {
      const vpcOrComputeService = services.find((service) =>
        service.href.includes("/vpc") || service.href.includes("/compute")
      );
      if (vpcOrComputeService) {
        selectItem("services", vpcOrComputeService.href, vpcOrComputeService.label || "Service");
      }
    }

    // Auto-select storage service when restrictToBucket is true
    if (restrictToBucket) {
      // Try S3 (AWS), then GCS (GCP), then Blob (Azure)
      const storageService = services.find((service) =>
        service.href.includes("/s3") ||
        service.href.includes("/gcs") ||
        service.href.includes("/storage")
      );
      if (storageService) {
        selectItem("services", storageService.href, storageService.label || "Storage");
      }
    }
  }, [restrictToVPCEndpointService, restrictToBucket, services, state.columns.services.selectedHref, selectItem]);

  const { currentData: regions, isLoading: regionsLoading } = useGetRegionsQuery(
    state.columns.services.selectedHref || "",
    { skip: !state.columns.services.selectedHref, refetchOnMountOrArgChange: true }
  );

  // Auto-select region when there's only one option (e.g., "Global" for S3)
  React.useEffect(() => {
    if (regions && regions.length === 1 && !state.columns.regions.selectedHref && state.columns.services.selectedHref) {
      const singleRegion = regions[0];
      selectItem("regions", singleRegion.href, singleRegion.label || "Region");
    }
  }, [regions, state.columns.regions.selectedHref, state.columns.services.selectedHref, selectItem]);

  const { currentData: allTypes, isLoading: typesLoading } = useGetTypesQuery(
    state.columns.regions.selectedHref || "",
    { skip: !state.columns.regions.selectedHref, refetchOnMountOrArgChange: true }
  );

  // Filter types based on restrictions
  const types = React.useMemo(() => {
    if (!allTypes) return undefined;
    return allTypes
      .filter((type) => {
        // Filter by dataTypeRestrictions
        if (dataTypeRestrictions.length > 0) {
          return dataTypeRestrictions.some((restriction) => type.href.includes(restriction));
        }
        return true;
      })
      .filter((type) => {
        // Filter to buckets only when restrictToBucket is true
        if (restrictToBucket) {
          return type.href.includes("/bucket") || type.href.includes("/container");
        }
        return true;
      })
      .filter((type) => {
        // Filter out roles for GCP resource
        if (forGcpResource.isActive) {
          return !type.href.includes("role");
        }
        return true;
      });
  }, [allTypes, dataTypeRestrictions, forGcpResource.isActive, restrictToBucket]);

  // Auto-select type when there's only one option (e.g., when filtering to buckets or vpces)
  React.useEffect(() => {
    if (types && types.length === 1 && !state.columns.types.selectedHref && state.columns.regions.selectedHref) {
      const singleType = types[0];
      selectItem("types", singleType.href, singleType.label || "Type");
    }
  }, [types, state.columns.types.selectedHref, state.columns.regions.selectedHref, selectItem]);

  const { currentData: dataItemsResult, isLoading: itemsLoading } = useGetDataItemsQuery(
    state.columns.types.selectedHref || "",
    { skip: !state.columns.types.selectedHref, refetchOnMountOrArgChange: true }
  );

  const dataItems = dataItemsResult?.items;

  // Children queries (for nested navigation)
  const selectedItem = React.useMemo(() => {
    if (!dataItems) return undefined;
    return dataItems.find((item) => item._cpln.href === state.columns.items.selectedHref);
  }, [dataItems, state.columns.items.selectedHref]);

  const { currentData: children, isLoading: childrenLoading } = useGetChildrenQuery(
    selectedItem?._cpln.href || "",
    { skip: !selectedItem?._cpln.hasChildren || !selectedItem?._cpln.href || restrictToParent, refetchOnMountOrArgChange: true }
  );

  const { currentData: nestedItems, isLoading: nestedItemsLoading } = useGetNestedItemsQuery(
    state.columns.children.selectedHref || "",
    { skip: !state.columns.children.selectedHref || restrictToParent, refetchOnMountOrArgChange: true }
  );

  // Calculate visible columns - always show columns to make the flow clear to users
  // Each column is visible when the previous column has a selection
  const visibleColumns = React.useMemo(() => {
    const cols: ColumnType[] = ["services"];

    if (state.columns.services.selectedHref) {
      cols.push("regions");
    }
    if (state.columns.regions.selectedHref) {
      cols.push("types");
    }
    if (state.columns.types.selectedHref) {
      cols.push("items");
    }
    if (!restrictToParent && state.columns.items.selectedHref && selectedItem?._cpln.hasChildren) {
      cols.push("children");
    }
    if (!restrictToParent && state.columns.children.selectedHref) {
      cols.push("nestedItems");
    }

    return cols;
  }, [
    state.columns,
    selectedItem,
    restrictToParent,
  ]);

  // Get filtered items for a column
  const getFilteredItems = React.useCallback(
    (column: ColumnType): (BrowserLink | BrowserData)[] => {
      let items: (BrowserLink | BrowserData)[] | undefined;

      switch (column) {
        case "services":
          items = services;
          break;
        case "regions":
          items = regions;
          break;
        case "types":
          items = types;
          break;
        case "items":
          items = dataItems;
          break;
        case "children":
          items = children;
          break;
        case "nestedItems":
          items = nestedItems;
          break;
      }

      if (!items) return [];

      const filter = state.columns[column].filter.toLowerCase();
      if (!filter) return items;

      return items.filter((item) => {
        const label = "_cpln" in item ? item._cpln.label : item.label || "";
        return label.toLowerCase().includes(filter);
      });
    },
    [services, regions, types, dataItems, children, nestedItems, state.columns]
  );

  const getFocusedIndex = React.useCallback(
    (column: ColumnType) => state.columns[column].focusedIndex,
    [state.columns]
  );

  // Handle item selection
  const handleItemClick = React.useCallback(
    (column: ColumnType, item: BrowserLink | BrowserData) => {
      const isData = item._type === "data";
      const label = isData ? (item as BrowserData)._cpln.label : (item as BrowserLink).label || "";
      const href = isData ? (item as BrowserData)._cpln.href || "" : (item as BrowserLink).href;

      // Clear selections when navigating
      clearSelections();

      // Select the item for navigation
      selectItem(column, href, label);

      // For data items in selectable columns, also add to selections
      if (isData && (column === "items" || column === "nestedItems")) {
        const dataItem = item as BrowserData;
        // Auto-select if: restrictToParent OR item has no children OR it's in nestedItems
        const shouldAutoSelect = restrictToParent || !dataItem._cpln.hasChildren || column === "nestedItems";
        if (shouldAutoSelect) {
          if (selectionType === "single") {
            setSingleSelection(dataItem);
          } else if (selectionType === "multi") {
            toggleSelection(dataItem);
          }
        }
      }
    },
    [selectItem, clearSelections, restrictToParent, selectionType, setSingleSelection, toggleSelection]
  );

  // Handle data item selection (checkbox/radio)
  const handleItemSelect = React.useCallback(
    (item: BrowserData) => {
      if (selectionType === "single") {
        setSingleSelection(item);
      } else if (selectionType === "multi") {
        toggleSelection(item);
      }
    },
    [selectionType, setSingleSelection, toggleSelection]
  );

  // Keyboard navigation
  const { handleKeyDown } = useKeyboardNavigation({
    activeColumn: state.activeColumn,
    visibleColumns,
    getFilteredItems,
    getFocusedIndex,
    setActiveColumn,
    setFocusedIndex,
    onSelectItem: handleItemClick,
    columnRefs,
    searchInputRefs,
  });

  // Handle confirm
  const handleConfirm = React.useCallback(async () => {
    try {
      setIsConfirmLoading(true);
      await onClose(state.selections);
    } catch (e: any) {
      notification.warning({
        message: "Failed",
        description: e?.message || "An error occurred",
      });
    } finally {
      setIsConfirmLoading(false);
    }
  }, [state.selections, onClose]);

  // Handle cancel
  const handleCancel = React.useCallback(() => {
    onClose([]);
  }, [onClose]);

  // Reset state when modal closes
  React.useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  // Focus first column when modal opens
  const hasFocusedOnOpen = React.useRef(false);
  React.useEffect(() => {
    if (!open) {
      hasFocusedOnOpen.current = false;
      return;
    }
    if (visibleColumns.length === 0 || hasFocusedOnOpen.current) {
      return;
    }
    hasFocusedOnOpen.current = true;
    // Small delay to ensure modal is fully rendered
    const timeoutId = setTimeout(() => {
      const firstColumn = visibleColumns[0];
      const columnEl = columnRefs.current.get(firstColumn);
      if (columnEl) {
        columnEl.focus();
      }
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [open, visibleColumns]);

  // Handle API errors
  React.useEffect(() => {
    if (servicesError) {
      const errorMessage = (servicesError as any)?.data?.message || "Failed to fetch services";
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
    }
  }, [servicesError]);

  return (
    <Modal
      title={title}
      open={open}
      onCancel={handleCancel}
      maskClosable={false}
      width={1200}
      className={styles.modal}
      styles={{ body: { padding: 0 } }}
      footer={null}
      destroyOnClose
    >
      <div className={styles.modalContent} onKeyDown={handleKeyDown}>
        <BrowserBreadcrumb items={state.breadcrumbs} onNavigate={navigateToBreadcrumb} />

        <div className={styles.columnsContainer}>
          {visibleColumns.includes("services") && (
            <BrowserColumn
              ref={(el) => { columnRefs.current.set("services", el); }}
              type="services"
              title="Services"
              items={services}
              isLoading={servicesLoading}
              isActive={state.activeColumn === "services"}
              selectedHref={state.columns.services.selectedHref}
              filter={state.columns.services.filter}
              focusedIndex={state.columns.services.focusedIndex}
              selectionType="none"
              selections={[]}
              isClickable={!restrictToBucket && !restrictToVPCEndpointService}
              onItemClick={(item) => handleItemClick("services", item)}
              onFilterChange={(filter) => setFilter("services", filter)}
              onFocusChange={(index) => setFocusedIndex("services", index)}
              onColumnFocus={() => setActiveColumn("services")}
            />
          )}

          {visibleColumns.includes("regions") && (
            <BrowserColumn
              ref={(el) => { columnRefs.current.set("regions", el); }}
              type="regions"
              title="Regions"
              items={regions}
              isLoading={regionsLoading}
              isActive={state.activeColumn === "regions"}
              selectedHref={state.columns.regions.selectedHref}
              filter={state.columns.regions.filter}
              focusedIndex={state.columns.regions.focusedIndex}
              selectionType="none"
              selections={[]}
              isClickable={!restrictToBucket}
              onItemClick={(item) => handleItemClick("regions", item)}
              onFilterChange={(filter) => setFilter("regions", filter)}
              onFocusChange={(index) => setFocusedIndex("regions", index)}
              onColumnFocus={() => setActiveColumn("regions")}
            />
          )}

          {visibleColumns.includes("types") && (
            <BrowserColumn
              ref={(el) => { columnRefs.current.set("types", el); }}
              type="types"
              title="Types"
              items={types}
              isLoading={typesLoading}
              isActive={state.activeColumn === "types"}
              selectedHref={state.columns.types.selectedHref}
              filter={state.columns.types.filter}
              focusedIndex={state.columns.types.focusedIndex}
              selectionType="none"
              selections={[]}
              isClickable={!restrictToBucket}
              onItemClick={(item) => handleItemClick("types", item)}
              onFilterChange={(filter) => setFilter("types", filter)}
              onFocusChange={(index) => setFocusedIndex("types", index)}
              onColumnFocus={() => setActiveColumn("types")}
            />
          )}

          {visibleColumns.includes("items") && (
            <BrowserColumn
              ref={(el) => { columnRefs.current.set("items", el); }}
              type="items"
              title={dataItemsResult?.label || "Items"}
              items={dataItems}
              isLoading={itemsLoading}
              isActive={state.activeColumn === "items"}
              selectedHref={state.columns.items.selectedHref}
              filter={state.columns.items.filter}
              focusedIndex={state.columns.items.focusedIndex}
              selectionType={selectionType}
              selections={state.selections}
              isClickable={!restrictToBucket}
              canNavigateDeeper={!restrictToParent}
              onItemClick={(item) => handleItemClick("items", item)}
              onItemSelect={(item) => handleItemSelect(item as BrowserData)}
              onFilterChange={(filter) => setFilter("items", filter)}
              onFocusChange={(index) => setFocusedIndex("items", index)}
              onColumnFocus={() => setActiveColumn("items")}
            />
          )}

          {visibleColumns.includes("children") && (
            <BrowserColumn
              ref={(el) => { columnRefs.current.set("children", el); }}
              type="children"
              title="Children"
              items={children}
              isLoading={childrenLoading}
              isActive={state.activeColumn === "children"}
              selectedHref={state.columns.children.selectedHref}
              filter={state.columns.children.filter}
              focusedIndex={state.columns.children.focusedIndex}
              selectionType="none"
              selections={[]}
              onItemClick={(item) => handleItemClick("children", item)}
              onFilterChange={(filter) => setFilter("children", filter)}
              onFocusChange={(index) => setFocusedIndex("children", index)}
              onColumnFocus={() => setActiveColumn("children")}
            />
          )}

          {visibleColumns.includes("nestedItems") && (
            <BrowserColumn
              ref={(el) => { columnRefs.current.set("nestedItems", el); }}
              type="nestedItems"
              title="Items"
              items={nestedItems}
              isLoading={nestedItemsLoading}
              isActive={state.activeColumn === "nestedItems"}
              selectedHref={state.columns.nestedItems.selectedHref}
              filter={state.columns.nestedItems.filter}
              focusedIndex={state.columns.nestedItems.focusedIndex}
              selectionType={selectionType}
              selections={state.selections}
              canNavigateDeeper={false}
              onItemClick={(item) => handleItemClick("nestedItems", item)}
              onItemSelect={(item) => handleItemSelect(item as BrowserData)}
              onFilterChange={(filter) => setFilter("nestedItems", filter)}
              onFocusChange={(index) => setFocusedIndex("nestedItems", index)}
              onColumnFocus={() => setActiveColumn("nestedItems")}
            />
          )}
        </div>

        <ModalFooter
          selectionType={selectionType}
          selectedCount={state.selections.length}
          isConfirmLoading={isConfirmLoading}
          onCancel={handleCancel}
          onConfirm={handleConfirm}
        />
      </div>
    </Modal>
  );
};
